import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import '../styles/TeamLabelCalendar.css';

/**
 * @param {{ columns: { id: string, name: string }[], selectedIds: Set<string> | null, onChange: (ids: Set<string> | null) => void }} props
 */
export default function TeamLabelStaffFilter({ columns, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const searchId = useId();

  const allIds = useMemo(() => new Set(columns.map((c) => c.id)), [columns]);

  const selectedCount = selectedIds == null ? columns.length : selectedIds.size;

  const toggleId = (id) => {
    if (selectedIds == null) {
      const next = new Set(allIds);
      next.delete(id);
      if (next.size === 0) return;
      onChange(next);
      return;
    }
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
      if (next.size === 0) {
        onChange(null);
        return;
      }
    } else {
      next.add(id);
      if (next.size === allIds.size) {
        onChange(null);
        return;
      }
    }
    onChange(next);
  };

  const selectAll = () => onChange(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDocPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      return undefined;
    }
    const id = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  const filteredColumns = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return columns;
    return columns.filter((c) => c.name.toLowerCase().includes(q));
  }, [columns, searchQuery]);

  if (columns.length === 0) return null;

  return (
    <details
      ref={rootRef}
      className="team-cal-v3-staff-filter"
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary className="team-cal-v3-staff-filter__summary" aria-label="Filter employees">
        <span className="team-cal-v3-staff-filter__label">Employee</span>
        <span className="team-cal-v3-staff-filter__count">
          {selectedIds == null ? `${columns.length} / ${columns.length}` : `${selectedCount} / ${columns.length}`}
        </span>
      </summary>
      <div className="team-cal-v3-staff-filter__panel">
        <div className="team-cal-v3-staff-filter__actions">
          <button type="button" className="team-cal-v3-staff-filter__action" onClick={selectAll}>
            Show all employees
          </button>
        </div>
        <label className="team-cal-v3-staff-filter__search-label" htmlFor={searchId}>
          <span className="team-cal-v3-staff-filter__search-hint">Search</span>
          <input
            id={searchId}
            ref={searchRef}
            type="search"
            className="team-cal-v3-staff-filter__search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name..."
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <ul className="team-cal-v3-staff-filter__list">
          {filteredColumns.length === 0 ? (
            <li className="team-cal-v3-staff-filter__empty">No employees match your search.</li>
          ) : (
            filteredColumns.map((c) => {
              const checked = selectedIds == null || selectedIds.has(c.id);
              return (
                <li key={c.id}>
                  <label className="team-cal-v3-staff-filter__row">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleId(c.id)}
                    />
                    <span className="team-cal-v3-staff-filter__name">{c.name}</span>
                  </label>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </details>
  );
}
